"use client";

import { useState } from "react";
import { FileText, Download, X } from "lucide-react";
import { VoiceMessagePlayer } from "./VoiceMessagePlayer";
import type { Message } from "./TeamChatService";

export interface AttachmentRendererProps {
  attachments: Message["attachments"];
  metadata?: Record<string, any>;
  /** An AI sticker is an uploaded image — render it bare, not as a file card. */
  asSticker?: boolean;
}

export function AttachmentRenderer({ attachments, metadata, asSticker }: AttachmentRendererProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  if (!attachments?.length) return null;

  const handleDownload = async (url: string, fileName: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch {
      window.open(url, "_blank");
    }
  };

  return (
    <>
      <div className={asSticker ? "mt-1" : "mt-2 space-y-2"}>
        {attachments.map(att => {
          const isImage = att.fileType?.startsWith('image/');
          const isAudio = att.fileType?.startsWith('audio/');
          const isVideo = att.fileType?.startsWith('video/');
          const isGif = att.fileUrl?.includes('.gif') || att.fileType === 'image/gif';

          if (isImage || isGif) {
            // Sticker: no frame, no lightbox, no download affordance — it is a
            // reaction, not a file someone attached.
            if (asSticker) {
              return (
                <img
                  key={att.id}
                  src={att.fileUrl}
                  alt="Sticker"
                  className="h-32 w-32 object-contain"
                />
              );
            }
            return (
              <button
                key={att.id}
                onClick={() => setLightboxUrl(att.fileUrl)}
                className="block cursor-pointer"
              >
                <img
                  src={att.fileUrl}
                  alt={att.fileName}
                  // h-64 not max-h-64: an unsized image measures zero until it
                  // decodes, and the message list scrolls before it grows.
                  className="h-64 w-auto max-w-xs rounded-lg border border-zinc-200 object-contain object-left dark:border-zinc-700 hover:brightness-90 transition-all"
                  loading="lazy"
                />
              </button>
            );
          }

          if (isAudio || metadata?.isVoiceMessage) {
            return <VoiceMessagePlayer key={att.id} src={att.fileUrl} fileType={att.fileType} />;
          }

          if (isVideo) {
            return (
              <video key={att.id} controls className="max-w-xs max-h-48 rounded-lg border border-zinc-200 dark:border-zinc-700" preload="metadata">
                <source src={att.fileUrl} type={att.fileType} />
              </video>
            );
          }

          // Generic file — button to hide Supabase URL
          return (
            <button
              key={att.id}
              onClick={() => handleDownload(att.fileUrl, att.fileName)}
              className="flex items-center gap-2 bg-zinc-100 dark:bg-zinc-800 rounded-lg px-3 py-2 max-w-xs hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-left cursor-pointer"
            >
              <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 truncate">{att.fileName}</p>
                {att.fileSize && <p className="text-[10px] text-zinc-400">{(att.fileSize / 1024).toFixed(0)} KB</p>}
              </div>
              <Download className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
            </button>
          );
        })}
      </div>

      {/* Image lightbox — portal to body so it covers entire screen */}
      {lightboxUrl && typeof document !== 'undefined' && (
        (() => {
          const ReactDOM = require('react-dom');
          return ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center cursor-pointer"
              onClick={() => setLightboxUrl(null)}
              style={{ margin: 0, padding: 0 }}
            >
              <button
                onClick={() => setLightboxUrl(null)}
                className="absolute top-6 right-6 p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <img
                src={lightboxUrl}
                alt="Full size"
                className="max-w-[85vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const fileName = lightboxUrl.split('/').pop() || 'image';
                  handleDownload(lightboxUrl, fileName);
                }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors backdrop-blur-sm"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>,
            document.body
          );
        })()
      )}
    </>
  );
}
